import React, { useState, useEffect } from "react";
import { Modal } from "@material-ui/core";
import styles from "./BulkCreateInvoicesModal.module.scss";
import Button from "../../button/Button";
import Select from "react-select";
import moment from "moment";
import { CloseIcon } from "../../../assets";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  bulkCreateBillingInvoices,
  bulkCreateBillingInvoicesResetState,
} from "../../../store/actions/admin/billingInvoices";

const BILLING_PERIOD_OPTIONS = [
  { value: "monthly", label: "Mesečno" },
  { value: "quarterly", label: "Kvartalno" },
  { value: "semi-annual", label: "Polugodišnje" },
  { value: "annual", label: "Godišnje" },
];

const BulkCreateInvoicesModal = ({ modalOpen, onClose, selectedSchemas, selectAll, allSchemaIds }) => {
  const dispatch = useDispatch();
  const bulkCreate = useSelector((state) => state.billingInvoices.bulkCreate);

  const [billingPeriodStart, setBillingPeriodStart] = useState("");
  const [billingPeriodEnd, setBillingPeriodEnd] = useState("");
  const [billingPeriodType, setBillingPeriodType] = useState({ value: "monthly", label: "Mesečno" });

  // Auto-calculate end date when period type or start date changes
  useEffect(() => {
    if (billingPeriodType && billingPeriodStart) {
      const startDate = moment(billingPeriodStart);
      let endDate;
      const periodType = typeof billingPeriodType === "object" ? billingPeriodType.value : billingPeriodType;
      switch (periodType) {
        case "monthly":
          endDate = startDate.clone().add(1, "month").subtract(1, "day").endOf("day");
          break;
        case "quarterly":
          endDate = startDate.clone().add(3, "months").subtract(1, "day").endOf("day");
          break;
        case "semi-annual":
          endDate = startDate.clone().add(6, "months").subtract(1, "day").endOf("day");
          break;
        case "annual":
          endDate = startDate.clone().add(1, "year").subtract(1, "day").endOf("day");
          break;
        default:
          return;
      }
      setBillingPeriodEnd(endDate.format("YYYY-MM-DD"));
    }
  }, [billingPeriodType, billingPeriodStart]);

  // Reset form when modal closes
  useEffect(() => {
    if (!modalOpen) {
      setBillingPeriodStart("");
      setBillingPeriodEnd("");
      setBillingPeriodType({ value: "monthly", label: "Mesečno" });
      dispatch(bulkCreateBillingInvoicesResetState());
    }
  }, [modalOpen, dispatch]);

  // Handle success/error
  useEffect(() => {
    if (bulkCreate.success) {
      const data = bulkCreate.data;
      if (data?.summary) {
        const { generated, skipped, errors } = data.summary;
        toast.success(
          `Uspešno kreirano: ${generated || 0}, Preskočeno: ${skipped || 0}, Greške: ${errors || 0}`
        );
      } else {
        toast.success("Fakture uspešno kreirane");
      }
      dispatch(bulkCreateBillingInvoicesResetState());
      onClose();
    }
  }, [bulkCreate.success, dispatch, onClose]);

  useEffect(() => {
    if (bulkCreate.error) {
      const errorMessage =
        bulkCreate.error?.response?.data?.message ||
        bulkCreate.error?.message ||
        "Greška pri kreiranju faktura";
      toast.error(errorMessage);
      dispatch(bulkCreateBillingInvoicesResetState());
    }
  }, [bulkCreate.error, dispatch]);

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (selectedSchemas.length === 0 && !selectAll) {
      toast.error("Molimo izaberite barem jednu šemu fakture");
      return;
    }

    if (!billingPeriodStart) {
      toast.error("Molimo unesite datum početka perioda");
      return;
    }

    if (!billingPeriodEnd) {
      toast.error("Molimo unesite datum završetka perioda");
      return;
    }

    // Get schema IDs - use allSchemaIds if selectAll is true, otherwise use selected schemas
    // Ensure all IDs are integers
    let schemaIds = [];

    if (selectAll) {
      schemaIds = allSchemaIds
        .map((id) => {
          const parsed = parseInt(id, 10);
          return !isNaN(parsed) && parsed > 0 ? parsed : null;
        })
        .filter((id) => id !== null);
    } else {
      schemaIds = selectedSchemas
        .map((s) => {
          if (!s || !s.id) return null;
          const parsed = parseInt(s.id, 10);
          return !isNaN(parsed) && parsed > 0 ? parsed : null;
        })
        .filter((id) => id !== null);
    }

    if (schemaIds.length === 0) {
      toast.error("Molimo izaberite barem jednu šemu fakture");
      return;
    }

    const bulkData = {
      schemaIds,
      billingPeriodStart: moment(billingPeriodStart).startOf("day").toISOString(),
      billingPeriodEnd: moment(billingPeriodEnd).endOf("day").toISOString(),
      billingPeriodType: typeof billingPeriodType === "object" ? billingPeriodType.value : billingPeriodType,
    };

    dispatch(bulkCreateBillingInvoices(bulkData));
  };

  const selectedCount = selectAll ? allSchemaIds.length : selectedSchemas.length;

  return (
    <Modal
      open={modalOpen}
      onClose={() => {
        onClose();
      }}
    >
      <div className={styles.modalWrap}>
        <div className={styles.modalInner}>
          <div className={styles.header}>
            <div className={styles.closeIconWrap}>
              <img
                src={CloseIcon}
                alt="close"
                onClick={() => {
                  onClose();
                }}
                className={styles.closeIcon}
              />
            </div>
            <p className={styles.title}>Masovno kreiranje faktura ({selectedCount})</p>
          </div>
          <div className={styles.content}>
            <div className={styles.formSection}>
              <h3 className={styles.sectionTitle}>Period fakturisanja</h3>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Tip perioda *</label>
                  <Select
                    options={BILLING_PERIOD_OPTIONS}
                    value={billingPeriodType}
                    onChange={(selectedOption) => {
                      setBillingPeriodType(selectedOption);
                      if (billingPeriodStart) {
                        const startDate = moment(billingPeriodStart);
                        let endDate;
                        switch (selectedOption.value) {
                          case "monthly":
                            endDate = startDate.clone().add(1, "month").subtract(1, "day").endOf("day");
                            break;
                          case "quarterly":
                            endDate = startDate.clone().add(3, "months").subtract(1, "day").endOf("day");
                            break;
                          case "semi-annual":
                            endDate = startDate.clone().add(6, "months").subtract(1, "day").endOf("day");
                            break;
                          case "annual":
                            endDate = startDate.clone().add(1, "year").subtract(1, "day").endOf("day");
                            break;
                          default:
                            return;
                        }
                        setBillingPeriodEnd(endDate.format("YYYY-MM-DD"));
                      }
                    }}
                    placeholder="Izaberite tip perioda"
                    styles={{
                      container: (provided) => ({
                        ...provided,
                        width: "100%",
                      }),
                      control: (provided) => ({
                        ...provided,
                        minHeight: "50px",
                        border: "1px solid #ced4da",
                        borderRadius: "4px",
                        "&:hover": {
                          borderColor: "#007bff",
                        },
                      }),
                    }}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Datum početka *</label>
                  <input
                    type="date"
                    className={styles.dateInput}
                    value={billingPeriodStart}
                    onChange={(e) => {
                      const value = e.target.value;
                      setBillingPeriodStart(value);
                      if (billingPeriodType && value) {
                        const startDate = moment(value);
                        let endDate;
                        const periodType = typeof billingPeriodType === "object" ? billingPeriodType.value : billingPeriodType;
                        switch (periodType) {
                          case "monthly":
                            endDate = startDate.clone().add(1, "month").subtract(1, "day").endOf("day");
                            break;
                          case "quarterly":
                            endDate = startDate.clone().add(3, "months").subtract(1, "day").endOf("day");
                            break;
                          case "semi-annual":
                            endDate = startDate.clone().add(6, "months").subtract(1, "day").endOf("day");
                            break;
                          case "annual":
                            endDate = startDate.clone().add(1, "year").subtract(1, "day").endOf("day");
                            break;
                          default:
                            return;
                        }
                        setBillingPeriodEnd(endDate.format("YYYY-MM-DD"));
                      }
                    }}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Datum završetka *</label>
                  <input
                    type="date"
                    className={styles.dateInput}
                    value={billingPeriodEnd}
                    onChange={(e) => setBillingPeriodEnd(e.target.value)}
                    min={billingPeriodStart}
                  />
                </div>
              </div>
            </div>

            <div className={styles.actions}>
              <Button
                name={bulkCreate.attempt ? "Kreiranje..." : "Kreiraj fakture"}
                onClick={handleSubmit}
                disabled={bulkCreate.attempt || selectedCount === 0}
                showLoader={bulkCreate.attempt}
              />
              <Button name="Otkaži" onClick={onClose} />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default BulkCreateInvoicesModal;

